import React, { useEffect, useState } from "react";
import { Smartphone, Download, Bell, BellOff, Share, CheckCircle2, Apple, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { pushSupported, isIOS, isStandalone, enablePush, disablePush, getSubscription } from "@/lib/push";

// Profile card: install to home screen (PWA) + enable/disable push notifications.
export default function MobileAppCard() {
  const [installable, setInstallable] = useState(!!window.deferredInstallPrompt);
  const [installed, setInstalled] = useState(isStandalone());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    const onInstallable = () => setInstallable(true);
    const onInstalled = () => { setInstalled(true); setInstallable(false); toast.success("App added to your device."); };
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    if (pushSupported()) getSubscription().then((s) => setSubscribed(!!s)).catch(() => {});
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const install = async () => {
    const dp = window.deferredInstallPrompt;
    if (dp) {
      dp.prompt();
      const { outcome } = await dp.userChoice;
      if (outcome === "accepted") { setInstalled(true); setInstallable(false); }
      window.deferredInstallPrompt = null;
    } else if (ios) {
      setShowIosHelp(true);
    } else {
      toast.info("Use your browser menu → \"Install app\" / \"Add to Home Screen\".");
    }
  };

  const toggleNotifications = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        await disablePush();
        setSubscribed(false);
        toast.success("Notifications turned off.");
      } else {
        await enablePush();
        setSubscribed(true);
        toast.success("Notifications enabled — you'll get messages even when the app is closed.");
      }
    } catch (e) {
      toast.error(e.message || "Could not update notifications.");
      if (ios && !isStandalone()) setShowIosHelp(true);
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/push/test");
      toast.success(`Test sent to ${data.devices} device${data.devices === 1 ? "" : "s"} — check your lock screen.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test. Enable notifications first.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rsp-card p-4 space-y-3" data-testid="mobile-app-card">
      <div className="font-head uppercase tracking-wide flex items-center gap-2"><Smartphone size={18} className="gold" /> Mobile App</div>
      <p className="text-[var(--rsp-muted)] text-xs">Add RSP Riverside to your phone and turn on notifications to get recruiter messages on your lock screen — even when the app is closed.</p>

      {/* Install / Add to Home Screen */}
      {installed ? (
        <div className="flex items-center gap-2 text-emerald-400 text-sm" data-testid="pwa-installed-note">
          <CheckCircle2 size={16} /> Installed on this device
        </div>
      ) : (
        <button onClick={install} className="btn-gold w-full py-2.5 flex items-center justify-center gap-2" data-testid="install-app-btn">
          <Download size={16} /> Add to Home Screen
        </button>
      )}

      {/* Notifications toggle */}
      <button onClick={toggleNotifications} disabled={busy || !pushSupported()}
        className="w-full py-2.5 rounded-lg border border-[var(--rsp-border)] text-[var(--rsp-cream)] hover:border-[var(--rsp-gold)] flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
        data-testid="notifications-toggle">
        {subscribed ? <BellOff size={16} /> : <Bell size={16} />}
        {busy ? "Working…" : subscribed ? "Turn Off Notifications" : "Enable Notifications"}
      </button>
      {subscribed && (
        <button onClick={sendTest} disabled={busy}
          className="w-full py-2.5 rounded-lg border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:border-[var(--rsp-gold)] hover:text-[var(--rsp-cream)] flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
          data-testid="notifications-test">
          <Send size={16} /> Send Me a Test Notification
        </button>
      )}
      {!pushSupported() && <p className="text-[var(--rsp-muted)] text-xs">Notifications aren't supported in this browser.</p>}

      {(showIosHelp || (ios && !installed)) && (
        <div className="rounded-lg border border-[var(--rsp-border)] bg-[var(--rsp-bg-2)] p-3 text-sm text-[var(--rsp-gray)] space-y-1.5" data-testid="ios-install-help">
          <div className="flex items-center gap-2 gold font-head uppercase text-xs tracking-wide"><Apple size={14} /> iPhone / iPad Setup</div>
          <div className="flex items-center gap-2">1. Tap the <Share size={14} className="inline" /> Share button in Safari</div>
          <div>2. Choose <span className="text-[var(--rsp-cream)]">"Add to Home Screen"</span></div>
          <div>3. Open the RSP icon, then tap <span className="text-[var(--rsp-cream)]">Enable Notifications</span> here</div>
        </div>
      )}
    </div>
  );
}
